import type { HistoryPoint, InsightsPayload, Snapshot } from "@weather-deck/shared";
import { el, replace } from "./dom";
import { buildDeltaCard } from "./delta";
import { buildHero } from "./hero";
import { buildConcurTiles } from "./tiles";
import { buildAirQuality } from "./airQuality";
import { buildForecastStrip } from "./forecastStrip";
import { buildDailyForecast } from "./dailyForecast";
import { buildAlertsPanel } from "./alerts";
import { buildHistoricalCompare } from "./historicalCompare";
import { buildHistoryTable } from "./historyTable";
import { buildNotable } from "./notable";
import { buildDayRibbon } from "./dayRibbon";
import { buildWaterCard } from "./water";

const API_BASE = (import.meta as unknown as { env: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ?? "";

/**
 * A failed /api/insights fetch must not break the rest of the page — the
 * Notable card is simply absent, same resilience model as loadHistory below.
 */
async function fetchInsights(): Promise<InsightsPayload | null> {
  try {
    const resp = await fetch(`${API_BASE}/api/insights`, { cache: "no-store" });
    const contentType = resp.headers.get("content-type") ?? "";
    if (!resp.ok || !contentType.includes("application/json")) return null;
    return (await resp.json()) as InsightsPayload;
  } catch {
    return null;
  }
}

async function load(): Promise<void> {
  const hero = document.querySelector<HTMLElement>("#hero");
  const app = document.querySelector<HTMLElement>("#app");
  const sources = document.querySelector<HTMLElement>("#sources");
  if (!hero || !app || !sources) return;

  try {
    const [resp, insights] = await Promise.all([
      fetch(`${API_BASE}/api/latest`, { cache: "no-store" }),
      fetchInsights()
    ]);
    const contentType = resp.headers.get("content-type") ?? "";
    if (!resp.ok || !contentType.includes("application/json")) {
      app.textContent = "Weather Deck: initializing API worker...";
      return;
    }
    const snap = (await resp.json()) as Snapshot;
    render(snap, hero, app, sources, insights);
    await loadHistory(snap);
  } catch (e: unknown) {
    const err = e as Error;
    app.textContent = `Weather Deck: ${err.message}`;
  }
}


/**
 * Every failure path here used to `return` silently, leaving the static
 * "Loading history..." placeholder up forever and the delta card — which has
 * no placeholder at all — simply absent, with nothing on screen saying why.
 * Now every path reports.
 */
async function loadHistory(snap: Snapshot): Promise<void> {
  const root = document.querySelector<HTMLElement>("#history-table-root");
  const fail = (msg: string): void => { if (root) root.textContent = msg; };

  let history: HistoryPoint[];
  try {
    const resp = await fetch(`${API_BASE}/api/history`, { cache: "no-store" });
    const contentType = resp.headers.get("content-type") ?? "";
    if (!resp.ok) return fail(`Could not load history — server returned ${resp.status}.`);
    if (!contentType.includes("application/json")) return fail("Could not load history — unexpected response.");
    history = (await resp.json()) as HistoryPoint[];
  } catch (e) {
    return fail(`Could not load history — ${e instanceof Error ? e.message : "network error"}.`);
  }

  if (!Array.isArray(history) || history.length === 0) {
    // Normal right after a deploy: the rolling window builds up one point per
    // cron run, so say that rather than implying a fault.
    return fail("No history yet — this fills in over the next few hours.");
  }

  try {
    const deltaRoot = document.querySelector<HTMLElement>(".delta-root");
    if (deltaRoot) deltaRoot.replaceChildren(buildDeltaCard(snap, history));
    if (root) root.replaceChildren(buildHistoryTable(history));
  } catch (e) {
    fail(`Could not render history — ${e instanceof Error ? e.message : "unknown error"}.`);
  }
}

function render(snap: Snapshot, hero: HTMLElement, app: HTMLElement, sources: HTMLElement, insights: InsightsPayload | null): void {
  replace(hero, buildHero(snap));
  const notable = insights ? buildNotable(insights) : null;
  replace(app,
    notable,
    insights?.dayPlan ? buildDayRibbon(insights.dayPlan) : null,
    buildConcurTiles(snap),
    // Filled once /api/history arrives — the gap needs 7 days, not one reading.
    el("div", { className: "delta-root" }),
    buildAirQuality(snap.air),
    buildWaterCard(snap.water),
    buildDailyForecast(snap.weather.daily, snap.weather.hourly),
    buildForecastStrip(snap.weather),
    buildHistoricalCompare(snap, API_BASE),
    buildAlertsPanel(API_BASE)
  );
  sources.textContent = "Sources: Open-Meteo · UBA · DWD · openSenseMap";
}

load().catch((e: Error) => {
  const app = document.querySelector<HTMLElement>("#app");
  if (app) app.textContent = `Error: ${e.message}`;
});

// The app was never actually registering its own service worker — only
// unregistering any that happened to exist (leftover from an earlier
// cleanup of a legacy SW, per HANDOFF_AGY.md). That meant offline caching
// and Web Push could never work on any device: navigator.serviceWorker.ready
// (which Enable Push waits on) had nothing to ever resolve against.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js").catch((e: unknown) => {
    console.error("service worker registration failed:", e);
  });
}
